import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { walletService } from '../services';
import type { SavedCard } from '../services/wallet.service';
import { getCardColor, formatCardExpiry } from '../utils/cardBrand';
import pagameService, { PagaMeLink } from '../services/pagame.service';
import { useToast } from '../contexts/ToastContext';
import { Card, LoaderComponent, Modal, Button } from '../components/ui';
import api from '../services/api';
import { fmtParede } from '../utils/tempoCv';
import { TfiCreditCard } from 'react-icons/tfi';
import {
  FiPlus,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiActivity,
  FiShield,
  FiLock,
  FiTrendingUp,
  FiTrendingDown,
  FiRefreshCw,
  FiTrash2,
  FiEdit2,
  FiCreditCard,
  FiLink,
  FiCopy,
  FiShare2,
} from 'react-icons/fi';

// Features only enabled in sandbox; disabled in production until ready
const IS_SANDBOX = !process.env.REACT_APP_FIREBASE_PROJECT_ID?.includes('prod');
const PAGAME_ENABLED = IS_SANDBOX;
const TOPUP_ENABLED = IS_SANDBOX;
// Transferência BCN — desativada em produção (aguarda credenciais BCN)
const TRANSFER_ENABLED = IS_SANDBOX;
// eCard SISP (tokenização) — ativo em produção; em sandbox ainda em configuração (OTP de teste)
const ECARD_CONFIGURING = IS_SANDBOX;

const Wallet: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState({
    saldo: 0,
    saldo_bonus: 0,
    saldo_bloqueado: 0,
    saldo_disponivel: 0,
    moeda: 'CVE',
    status: 'ACTIVE',
  });
  const [stats, setStats] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txFilter, setTxFilter] = useState<string>('');

  // Cards
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [deleteCardId, setDeleteCardId] = useState<string | null>(null);

  // Modals
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [topupStep, setTopupStep] = useState<'input' | 'quote' | 'executing' | 'success'>('input');
  const [topupQuote, setTopupQuote] = useState<{
    quoteId: string; grossAmountCVE: number; feeCVE: number;
    netCreditedCVE: number; disclosureText: string; expiresAt: string;
  } | null>(null);
  const [topupSuccessAmount, setTopupSuccessAmount] = useState<number>(0);

  // BCN top-up (transferência bancária)
  const [bcnModalOpen, setBcnModalOpen]         = useState(false);
  const [bcnAmount, setBcnAmount]               = useState('');
  const [bcnStep, setBcnStep]                   = useState<'input' | 'pending' | 'confirmed'>('input');
  const [bcnData, setBcnData]                   = useState<any>(null);
  const [bcnLoading, setBcnLoading]             = useState(false);
  const bcnPollRef                              = useRef<ReturnType<typeof setInterval> | null>(null);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [deleteCardModalOpen, setDeleteCardModalOpen] = useState(false);
  const [renameCardId, setRenameCardId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [addCardInfoOpen, setAddCardInfoOpen] = useState(false);
  // Trava contra tentativas condenadas: a tokenização SISP só aceita Vinti4.
  // 14-15/10 houve uma onda de enrollments cancelados na página SISP (cartões
  // estrangeiros); o utilizador tem de confirmar a rede antes de continuar.
  const [vinti4Confirmado, setVinti4Confirmado] = useState(false);
  const [pagameModalOpen, setPagameModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // PagaMe
  const [pagameLinks, setPagameLinks] = useState<PagaMeLink[]>([]);
  const [pagameLoading, setPagameLoading] = useState(false);
  const [pagameForm, setPagameForm] = useState({ descricao: '', valor: '', tipo: 'MULTIPLO' as 'UNICO' | 'MULTIPLO' });
  const [createdLink, setCreatedLink] = useState<PagaMeLink | null>(null);

  // Form states
  const [topupAmount, setTopupAmount] = useState('');
  const [topupError, setTopupError] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  // QR Pay via token
  const [qrPayOpen, setQrPayOpen] = useState(false);
  const [qrPayRef, setQrPayRef] = useState('');
  const [qrPayDetails, setQrPayDetails] = useState<{ amount: number; entityCode?: string; referenceNumber?: string } | null>(null);
  const [qrPayCard, setQrPayCard] = useState<string | null>(null);
  const [qrPayLoading, setQrPayLoading] = useState(false);
  const [qrPayResult, setQrPayResult] = useState<{ success: boolean; message?: string } | null>(null);
  const [pinData, setPinData] = useState({ pin_atual: '', novo_pin: '', confirmar_pin: '' });

  useEffect(() => {
    if (addCardInfoOpen) setVinti4Confirmado(false);
  }, [addCardInfoOpen]);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Wallet');
    loadData();

    // Regresso do gateway SISP após pagamento bem-sucedido
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') === 'complete') {
      showSuccess(t('wallet.topupSuccess', { amount: '' }).replace('  ', ' ').trim());
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Pagamento QR/DUC com token
    const payQr = params.get('pay_qr');
    if (payQr) {
      setQrPayRef(payQr);
      setQrPayOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [balanceData, statsData] = await Promise.allSettled([
        walletService.getBalance(),
        walletService.getStats(),
      ]);
      if (balanceData.status === 'fulfilled') setBalance(balanceData.value);
      if (statsData.status === 'fulfilled') setStats(statsData.value);
      loadTransactions();
      loadCards();
      loadPagameLinks();
    } catch (err) {
      console.error('Error loading wallet:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (tipo?: string) => {
    setTxLoading(true);
    try {
      const result = await walletService.getTransactions({
        limit: 20,
        tipo: tipo || undefined,
      });
      setTransactions(result.data);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  const loadCards = async () => {
    setCardsLoading(true);
    try {
      const cards = await walletService.getCards();
      setSavedCards(cards);
    } catch (err) {
      console.error('Error loading cards:', err);
      setSavedCards([]);
    } finally {
      setCardsLoading(false);
    }
  };

  useEffect(() => {
    if (!qrPayOpen || !qrPayRef) return;
    setQrPayDetails(null);
    api.get(`/v1/sisp/pay/${qrPayRef}/details`)
      .then((r: any) => setQrPayDetails(r.data))
      .catch(() => {});
  }, [qrPayOpen, qrPayRef]);

  const handleQrTokenPay = async () => {
    if (!qrPayCard) return;
    setQrPayLoading(true);
    setQrPayResult(null);
    try {
      const res = await api.post(`/v1/sisp/pay/${qrPayRef}/token`, { tokenId: qrPayCard });
      const ok = res.data?.success;
      setQrPayResult({ success: ok, message: res.data?.error || res.data?.message });
      if (ok) loadTransactions();
    } catch (e: any) {
      setQrPayResult({ success: false, message: e.response?.data?.error || 'Erro no pagamento. Tente novamente.' });
    } finally {
      setQrPayLoading(false);
    }
  };

  const closeQrPay = () => { setQrPayOpen(false); setQrPayResult(null); setQrPayCard(null); };

  const handleAddCard = () => {
    setAddCardInfoOpen(true);
  };

  // A CTA do dashboard encaminha para cá com esta intenção, para quem ainda não
  // tem Pagali Card entrar directamente no enrolment em vez de o ir procurar.
  // O state é limpo a seguir: um refresh não deve reabrir o diálogo.
  useEffect(() => {
    if ((location.state as any)?.abrirPagaliCard) {
      setAddCardInfoOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const handleAddCardConfirm = async () => {
    setAddCardInfoOpen(false);
    setSaving(true);
    // Abrir o popup SÍNCRONO, dentro do gesto do utilizador — depois do await
    // os browsers bloqueiam window.open silenciosamente e "nada acontece".
    const w = 500;
    const h = 650;
    const left = (window.screen.width - w) / 2;
    const top = (window.screen.height - h) / 2;
    const popup = window.open(
      'about:blank',
      'pagali_sisp_tokenization',
      `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    if (popup) {
      try {
        popup.document.write(
          '<html><body style="font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#6B5F80"><p>A preparar a p&aacute;gina segura do SISP...</p></body></html>'
        );
      } catch { /* cross-origin — ignorar */ }
    }
    try {
      const result = await walletService.addCard();
      if (result.redirectUrl) {
        if (popup && !popup.closed) {
          popup.location.href = result.redirectUrl;
          // Monitorar quando o popup fecha (user cancelou ou completou)
          const checkInterval = setInterval(() => {
            if (!popup || popup.closed) {
              clearInterval(checkInterval);
              setSaving(false);
              // Recarregar cartões para ver se o novo token foi criado
              loadCards();
            }
          }, 1000);
        } else {
          // Popup bloqueado pelo browser — abrir na mesma janela
          window.location.href = result.redirectUrl;
        }
      } else {
        if (popup) popup.close();
        showError(t('wallet.tokenizationNotAvailable'));
        setSaving(false);
      }
    } catch (err: any) {
      if (popup) popup.close();
      const msg = err?.message || t('wallet.errorAddingCard');
      if (msg.includes('tokenização não encontrada') || msg.includes('não tem cliente')) {
        showError(t('wallet.cardServiceNotConfigured'));
      } else {
        showError(msg);
      }
      setSaving(false);
    }
  };

  // ─── PagaMe ───
  const loadPagameLinks = async () => {
    setPagameLoading(true);
    try {
      const links = await pagameService.getLinks();
      setPagameLinks(links);
    } catch { /* ignore */ }
    finally { setPagameLoading(false); }
  };

  const handleCreatePagame = async () => {
    if (!pagameForm.descricao.trim()) {
      showError('Insere uma descrição');
      return;
    }
    setSaving(true);
    try {
      const link = await pagameService.createLink({
        descricao: pagameForm.descricao.trim(),
        valor: pagameForm.valor ? parseFloat(pagameForm.valor) : null,
        tipo: pagameForm.tipo,
      });
      setCreatedLink(link);
      setPagameForm({ descricao: '', valor: '', tipo: 'MULTIPLO' });
      loadPagameLinks();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erro ao criar link');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    showSuccess('Link copiado!');
  };

  const handleShareLink = (url: string, descricao: string) => {
    if (navigator.share) {
      navigator.share({ title: 'PagaMe', text: descricao, url }).catch((err) => {
        if (err?.name !== 'AbortError') handleCopyLink(url);
      });
    } else {
      handleCopyLink(url);
    }
  };

  const handleCancelPagame = async (linkId: string) => {
    try {
      await pagameService.cancelLink(linkId);
      showSuccess('Link cancelado');
      loadPagameLinks();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erro ao cancelar');
    }
  };

  const handleRenameCard = async () => {
    if (!renameCardId) return;
    setSaving(true);
    try {
      await walletService.updateCardAlias(renameCardId, renameValue.trim());
      showSuccess(t('wallet.cardRenamed'));
      setRenameModalOpen(false);
      setRenameCardId(null);
      loadCards();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('wallet.errorRenamingCard'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!deleteCardId) return;
    setSaving(true);
    try {
      await walletService.removeCard(deleteCardId);
      showSuccess(t('wallet.cardRemoved'));
      setDeleteCardModalOpen(false);
      setDeleteCardId(null);
      loadCards();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('wallet.errorRemovingCard'));
    } finally {
      setSaving(false);
    }
  };


  const closeTopupModal = () => {
    setTopupModalOpen(false);
    setTopupStep('input');
    setTopupQuote(null);
    setTopupAmount('');
    setSelectedCardId(null);
    setTopupError(null);
    setTopupSuccessAmount(0);
  };

  const handleTopup = async () => {
    if (topupStep === 'input') {
      // Passo 1: obter cotação
      const valor = parseFloat(topupAmount);
      if (!valor || valor <= 0) { showError(t('wallet.enterValidValue')); return; }
      setSaving(true);
      try {
        const q = await walletService.createQuote(valor, 'VINTI4');
        setTopupQuote(q);
        setTopupStep('quote');
        await walletService.markQuoteDisplayed(q.quoteId);
      } catch (err) {
        showError(err instanceof Error ? err.message : t('wallet.errorTopup'));
      } finally {
        setSaving(false);
      }
      return;
    }

    if (topupStep === 'quote' && topupQuote) {
      // Passo 2: confirmar e executar
      const quoteId = topupQuote.quoteId;
      const netCVE = topupQuote.netCreditedCVE;
      setSaving(true);
      setTopupStep('executing');
      try {
        await walletService.acceptQuote(quoteId);
        const idempotencyKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await walletService.executeTopUp(quoteId, idempotencyKey);
        setTopupSuccessAmount(netCVE);
        setTopupStep('success');
        loadData();
      } catch (err: any) {
        const msg = err?.message || 'Erro ao executar carregamento';
        console.error('[topup] erro:', msg, err);
        setTopupStep('quote');
        setTopupError(msg);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleBcnTopupInitiate = async () => {
    const valor = parseFloat(bcnAmount);
    if (!valor || valor <= 0) { showError('Introduz um valor válido'); return; }
    setBcnLoading(true);
    try {
      const res = await api.post('/client/nfc/topup/initiate', { amount_cve: valor });
      setBcnData(res.data);
      setBcnStep('pending');
      // Polling a cada 30s
      bcnPollRef.current = setInterval(async () => {
        try {
          const poll = await api.get(`/client/nfc/topup/${res.data.ref}/status`);
          if (poll.data.credited) {
            clearInterval(bcnPollRef.current!);
            setBcnStep('confirmed');
            loadData();
          }
        } catch { /* silencioso */ }
      }, 30_000);
    } catch (err: any) {
      showError(err?.response?.data?.error || 'Erro ao iniciar carregamento');
    } finally {
      setBcnLoading(false);
    }
  };

  const closeBcnModal = () => {
    if (bcnPollRef.current) clearInterval(bcnPollRef.current);
    setBcnModalOpen(false);
    setBcnStep('input');
    setBcnAmount('');
    setBcnData(null);
  };

  const handleSetPin = async () => {
    if (pinData.novo_pin.length !== 4 || !/^\d{4}$/.test(pinData.novo_pin)) {
      showError(t('wallet.pinMust4Digits'));
      return;
    }
    if (pinData.novo_pin !== pinData.confirmar_pin) {
      showError(t('wallet.pinsDontMatch'));
      return;
    }
    setSaving(true);
    try {
      await walletService.setPin(pinData.novo_pin, pinData.pin_atual || undefined);
      showSuccess(t('wallet.pinConfigured'));
      setPinModalOpen(false);
      setPinData({ pin_atual: '', novo_pin: '', confirmar_pin: '' });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('wallet.errorConfiguringPin'));
    } finally {
      setSaving(false);
    }
  };

  const formatAmount = (amount: number | string | undefined | null): string => {
    const parsed = typeof amount === 'number' ? amount : parseFloat(String(amount ?? 0));
    const value = isNaN(parsed) ? 0 : parsed;
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('pt-CV', {
      timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (tipo: string) => {
    switch (tipo) {
      case 'TOPUP':
        return <FiArrowDownLeft className="text-green-600" />;
      case 'PAYMENT':
        return <FiArrowUpRight className="text-red-500" />;
      case 'TRANSFER_OUT':
        return <FiArrowUpRight className="text-orange-500" />;
      case 'TRANSFER_IN':
        return <FiArrowDownLeft className="text-blue-500" />;
      default:
        return <FiActivity className="text-gray-500" />;
    }
  };

  const getTransactionColor = (tipo: string) => {
    switch (tipo) {
      case 'TOPUP':
      case 'TRANSFER_IN':
        return 'text-green-600';
      case 'PAYMENT':
      case 'TRANSFER_OUT':
        return 'text-red-600';
      default:
        return 'text-pagali-gray-800';
    }
  };

  const getTransactionLabel = (tipo: string) => {
    switch (tipo) {
      case 'TOPUP':
        return t('wallet.topupLabel');
      case 'PAYMENT':
        return t('wallet.paymentLabel');
      case 'TRANSFER_OUT':
        return t('wallet.sendLabel');
      case 'TRANSFER_IN':
        return t('wallet.receivedLabel');
      default:
        return tipo;
    }
  };

  const getTransactionSign = (tipo: string) => {
    return tipo === 'TOPUP' || tipo === 'TRANSFER_IN' ? '+' : '-';
  };

  if (loading) {
    return <LoaderComponent size="lg" text={t('wallet.loadingWallet')} />;
  }

  return (
    <div className="space-y-6">
      {/* Wallet Pagali — saldo digital */}
      <Card className="bg-gradient-to-br from-pagali-purple via-indigo-600 to-pagali-primary text-white overflow-hidden relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full" />
          <div className="absolute -bottom-24 -left-12 w-48 h-48 bg-white/5 rounded-full" />
        </div>
        <div className="relative">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <TfiCreditCard className="text-lg" />
              <span className="text-white/70 text-sm font-semibold">Wallet Pagali</span>
            </div>
            <span
              className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                balance.status === 'ACTIVE'
                  ? 'bg-green-400/20 text-green-200'
                  : 'bg-red-400/20 text-red-200'
              }`}
            >
              {balance.status === 'ACTIVE' ? t('wallet.activeStatus') : t('wallet.blockedStatus')}
            </span>
          </div>
          <p className="text-xs text-white/50 mb-1">Saldo disponível na rede Pagali Pay</p>
          <p className="text-4xl font-bold mt-2">{formatAmount(balance.saldo_disponivel)}</p>
          <div className="flex items-center gap-4 mt-3 text-sm text-white/60">
            <span>{t('wallet.balance')}: {formatAmount(balance.saldo)}</span>
            {balance.saldo_bonus > 0 && <span>{t('wallet.bonus')}: {formatAmount(balance.saldo_bonus)}</span>}
            {balance.saldo_bloqueado > 0 && (
              <span>{t('wallet.blocked')}: {formatAmount(balance.saldo_bloqueado)}</span>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3 mt-3">
            {TOPUP_ENABLED ? (
              <button
                onClick={() => setTopupModalOpen(true)}
                className="flex flex-1 flex-col items-center gap-1.5 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <FiPlus className="text-xl" />
                <span className="text-xs font-medium">{t('wallet.topup')}</span>
              </button>
            ) : (
              <button
                disabled
                className="flex flex-1 flex-col items-center gap-1.5 py-3 bg-white/5 rounded-xl transition-colors opacity-40 cursor-not-allowed"
              >
                <FiPlus className="text-xl" />
                <span className="text-xs font-medium">{t('wallet.topup')}</span>
              </button>
            )}
            {TRANSFER_ENABLED ? (
              <button
                onClick={() => { setBcnStep('input'); setBcnAmount(''); setBcnData(null); setBcnModalOpen(true); }}
                className="flex flex-1 flex-col items-center gap-1.5 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <FiArrowDownLeft className="text-xl" />
                <span className="text-xs font-medium">Transferência</span>
              </button>
            ) : (
              <button
                disabled
                className="flex flex-1 flex-col items-center gap-1.5 py-3 bg-white/5 rounded-xl transition-colors opacity-40 cursor-not-allowed"
              >
                <FiArrowDownLeft className="text-xl" />
                <span className="text-xs font-medium">Transferência</span>
              </button>
            )}
            {PAGAME_ENABLED && (
              <button
                onClick={() => { setCreatedLink(null); setPagameModalOpen(true); }}
                className="flex flex-1 flex-col items-center gap-1.5 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <FiLink className="text-xl" />
                <span className="text-xs font-medium">PagaMe</span>
              </button>
            )}
            <button
              onClick={() => setPinModalOpen(true)}
              className="flex flex-1 flex-col items-center gap-1.5 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <FiLock className="text-xl" />
              <span className="text-xs font-medium">{t('wallet.pin')}</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-50 rounded-xl">
                <FiTrendingUp className="text-lg text-green-600" />
              </div>
              <div>
                <p className="text-xs text-pagali-gray-500">{t('wallet.loaded')}</p>
                <p className="font-bold text-pagali-gray-800">{formatAmount(stats.total_carregado)}</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-50 rounded-xl">
                <FiTrendingDown className="text-lg text-red-500" />
              </div>
              <div>
                <p className="text-xs text-pagali-gray-500">{t('wallet.spent')}</p>
                <p className="font-bold text-pagali-gray-800">{formatAmount(stats.total_gasto)}</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl">
                <FiArrowUpRight className="text-lg text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-pagali-gray-500">{t('wallet.sent')}</p>
                <p className="font-bold text-pagali-gray-800">{formatAmount(stats.total_enviado)}</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-50 rounded-xl">
                <FiArrowDownLeft className="text-lg text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-pagali-gray-500">{t('wallet.received')}</p>
                <p className="font-bold text-pagali-gray-800">{formatAmount(stats.total_recebido)}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Limits Info */}
      {stats?.limites && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FiShield className="text-pagali-primary" />
            <h3 className="font-semibold text-pagali-gray-800">{t('wallet.limits')}</h3>
            <span className="ml-auto text-xs bg-pagali-primary/10 text-pagali-primary px-2 py-0.5 rounded-full font-medium">
              {t('wallet.levelLabel')}: {stats.nivel_verificacao || t('wallet.basic')}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-pagali-gray-500">{t('wallet.perTransaction')}</p>
              <p className="font-semibold">{formatAmount(stats.limites.transacao)}</p>
            </div>
            <div>
              <p className="text-xs text-pagali-gray-500">{t('wallet.daily')}</p>
              <p className="font-semibold">{formatAmount(stats.limites.diario)}</p>
            </div>
            <div>
              <p className="text-xs text-pagali-gray-500">{t('wallet.monthly')}</p>
              <p className="font-semibold">{formatAmount(stats.limites.mensal)}</p>
            </div>
          </div>
        </Card>
      )}

      {/* eCard SISP */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiCreditCard className="text-pagali-primary" />
            <h3 className="font-semibold text-pagali-gray-800">Pagali Card</h3>
            {ECARD_CONFIGURING && (
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700">
                Em configuração
              </span>
            )}
          </div>
          <Button onClick={handleAddCard} disabled={saving}>
            <FiPlus className="mr-1" /> {t('wallet.addCard')}
          </Button>
        </div>

        {cardsLoading ? (
          <LoaderComponent size="sm" />
        ) : savedCards.length === 0 ? (
          <div className="text-center py-10">
            <FiCreditCard className="text-4xl text-pagali-gray-300 mx-auto mb-3" />
            <p className="text-pagali-gray-500">{t('wallet.noCardsSaved')}</p>
            <p className="text-sm text-pagali-gray-400 mt-1">
              {t('wallet.noCardsDesc')}
            </p>
            <button
              onClick={handleAddCard}
              disabled={saving}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-pagali-primary/10 text-pagali-primary rounded-xl text-sm font-medium hover:bg-pagali-primary/20 transition-colors"
            >
              <FiPlus /> {saving ? t('common.processing') : t('wallet.addFirstCard')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedCards.map((card, cardIndex) => {
              // Posição entre cartões da mesma bandeira: com vários cartões
              // iguais, cada um recebe uma variante de cor distinta
              const brandVariant = savedCards
                .slice(0, cardIndex)
                .filter((c) => (c.cardType || '').toUpperCase() === (card.cardType || '').toUpperCase()).length;
              // Calcular percentagem de uso dos limites SISP
              const usedTx = card.usedTransactions || 0;
              const usedAmt = Number(card.usedAmount) || 0;
              const maxTx = card.maxTransactions || 0;
              const maxAmt = Number(card.maxAmount) || 0;
              // Formato CVE: milhares com ponto, sem casas decimais (ex: 30.000)
              const fmtMil = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
              const pctTx = maxTx > 0 ? Math.min(100, Math.round((usedTx / maxTx) * 100)) : 0;
              const pctAmt = maxAmt > 0 ? Math.min(100, Math.round((usedAmt / maxAmt) * 100)) : 0;
              const pct = Math.max(pctTx, pctAmt);
              const needsRenewal = pct >= 80 || (card.limitDate && new Date(card.limitDate).getTime() - Date.now() < 30 * 24 * 3600 * 1000);
              const hasLimits = maxTx > 0 || maxAmt > 0;

              return (
              <div
                key={card.id}
                className={`relative rounded-2xl p-5 text-white overflow-hidden bg-gradient-to-br ${getCardColor(card.cardType, brandVariant)}`}
              >
                {/* Decorative */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white transform translate-x-1/4 -translate-y-1/4" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white transform -translate-x-1/4 translate-y-1/4" />
                </div>

                <div className="relative">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs text-white/70 uppercase tracking-wider font-medium truncate max-w-[180px]">
                      {card.alias || card.cardType || 'Cartão'}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          setRenameCardId(card.id);
                          setRenameValue(card.alias || '');
                          setRenameModalOpen(true);
                        }}
                        className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                        title={t('wallet.renameCard')}
                      >
                        <FiEdit2 className="text-sm" />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteCardId(card.id);
                          setDeleteCardModalOpen(true);
                        }}
                        className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                        title="Remover cartão"
                      >
                        <FiTrash2 className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Card Number */}
                  <p className="text-lg tracking-[0.25em] font-mono mb-4">
                    •••• •••• •••• {card.lastFourDigits}
                  </p>

                  {/* Footer */}
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-[10px] text-white/60 uppercase">{t('wallet.cardHolder')}</p>
                      <p className="text-sm font-medium truncate max-w-[180px]">
                        {card.cardHolder || t('wallet.cardHolder')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-white/60 uppercase">{t('wallet.validThru')}</p>
                      <p className="text-sm font-medium">
                        {formatCardExpiry(card)}
                      </p>
                    </div>
                  </div>

                  {/* Limites SISP + Barra de progresso */}
                  {hasLimits && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-white/70 uppercase">Limites SISP</span>
                        <span className="text-[10px] font-semibold">{pct}% usado</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-1.5 mb-2 overflow-hidden">
                        <div
                          className={`h-1.5 rounded-full transition-all ${pct >= 80 ? 'bg-amber-400' : 'bg-white'}`}
                          style={{ width: `${Math.max(pct, 3)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-white/80">
                        {maxTx > 0 && <span>{usedTx}/{maxTx} pagamentos</span>}
                        {maxAmt > 0 && <span>{fmtMil(usedAmt)}/{fmtMil(maxAmt)}$</span>}
                      </div>
                      {needsRenewal && (
                        <button
                          onClick={handleAddCard}
                          className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors text-xs font-semibold"
                          title="Renovar limites do cartão"
                        >
                          <FiRefreshCw className="text-xs" /> Renovar limites
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {/* Add card placeholder */}
            <button
              onClick={handleAddCard}
              disabled={saving}
              className="border-2 border-dashed border-pagali-gray-300 rounded-2xl flex flex-col items-center justify-center min-h-[180px] hover:border-pagali-primary hover:bg-pagali-primary/5 transition-all group"
            >
              <FiPlus className="text-2xl text-pagali-gray-400 group-hover:text-pagali-primary mb-2" />
              <span className="text-sm text-pagali-gray-500 group-hover:text-pagali-primary font-medium">
                {saving ? t('common.processing') : t('wallet.newCard')}
              </span>
            </button>
          </div>
        )}
      </Card>

      {/* PagaMe - Links de Pagamento (only in sandbox) */}
      {PAGAME_ENABLED && (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiLink className="text-lg text-purple-500" />
            <h3 className="font-semibold text-pagali-gray-800">PagaMe</h3>
          </div>
          <Button size="sm" onClick={() => { setCreatedLink(null); setPagameModalOpen(true); }}>
            <FiPlus className="mr-1" /> Criar Link
          </Button>
        </div>

        {pagameLoading ? (
          <div className="flex justify-center py-6"><LoaderComponent /></div>
        ) : pagameLinks.length === 0 ? (
          <div className="text-center py-8">
            <FiLink className="text-4xl text-pagali-gray-300 mx-auto mb-3" />
            <p className="text-pagali-gray-500 mb-1">Nenhum link de pagamento</p>
            <p className="text-pagali-gray-400 text-sm mb-4">
              Cria um link e partilha por WhatsApp, SMS ou email para receber pagamentos
            </p>
            <Button size="sm" variant="ghost" onClick={() => setPagameModalOpen(true)}>
              <FiPlus className="mr-1" /> Criar primeiro link
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {pagameLinks.map((link) => (
              <div key={link.id} className="flex items-center justify-between p-3 bg-pagali-gray-50 rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-pagali-gray-800 truncate">{link.descricao}</p>
                  <div className="flex items-center gap-3 text-xs text-pagali-gray-500 mt-1">
                    <span>{link.valor ? `${link.valor.toLocaleString()}$00` : 'Valor aberto'}</span>
                    <span>{link.totalPagamentos} pagamento{link.totalPagamentos !== 1 ? 's' : ''}</span>
                    {link.totalRecebido > 0 && (
                      <span className="text-green-600 font-medium">+{link.totalRecebido.toLocaleString()}$00</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    link.estado === 'ACTIVO' ? 'bg-green-100 text-green-700' :
                    link.estado === 'PAGO' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {link.estado === 'ACTIVO' ? 'Activo' : link.estado === 'PAGO' ? 'Pago' : link.estado}
                  </span>
                  {link.estado === 'ACTIVO' && (
                    <>
                      <button onClick={() => handleCopyLink(link.url)} className="p-1.5 hover:bg-pagali-gray-200 rounded-lg" title="Copiar link">
                        <FiCopy className="text-pagali-gray-500" />
                      </button>
                      <button onClick={() => handleShareLink(link.url, link.descricao)} className="p-1.5 hover:bg-pagali-gray-200 rounded-lg" title="Partilhar">
                        <FiShare2 className="text-pagali-gray-500" />
                      </button>
                      <button onClick={() => handleCancelPagame(link.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Cancelar">
                        <FiTrash2 className="text-red-400" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      {/* Transactions */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiActivity className="text-pagali-primary" />
            <h3 className="font-semibold text-pagali-gray-800">{t('wallet.history')}</h3>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={txFilter}
              onChange={(e) => {
                setTxFilter(e.target.value);
                loadTransactions(e.target.value);
              }}
              className="text-xs border border-pagali-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-pagali-primary"
            >
              <option value="">{t('common.all')}</option>
              <option value="TOPUP">{t('wallet.topups')}</option>
              <option value="PAYMENT">{t('nav.payments')}</option>
              <option value="TRANSFER_OUT">{t('wallet.sentItems')}</option>
              <option value="TRANSFER_IN">{t('wallet.receivedItems')}</option>
            </select>
            <button
              onClick={() => loadTransactions(txFilter)}
              className="p-1.5 rounded-lg hover:bg-pagali-gray-100 text-pagali-gray-500 transition-colors"
            >
              <FiRefreshCw className="text-sm" />
            </button>
          </div>
        </div>

        {txLoading ? (
          <LoaderComponent size="sm" />
        ) : transactions.length === 0 ? (
          <div className="text-center py-10">
            <FiActivity className="text-4xl text-pagali-gray-300 mx-auto mb-3" />
            <p className="text-pagali-gray-500">{t('wallet.noTransactions')}</p>
            <p className="text-sm text-pagali-gray-400 mt-1">
              {t('wallet.topupToStart')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-pagali-gray-50 -mx-4 md:-mx-6">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center gap-3 px-4 md:px-6 py-3 hover:bg-pagali-gray-50 transition-colors">
                <div className="w-10 h-10 rounded-full bg-pagali-gray-50 flex items-center justify-center flex-shrink-0">
                  {getTransactionIcon(tx.tipo)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-pagali-gray-800 truncate">
                    {tx.descricao || getTransactionLabel(tx.tipo)}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-pagali-gray-400">
                    <span>{getTransactionLabel(tx.tipo)}</span>
                    <span>•</span>
                    <span>{formatDate(tx.created_at)}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-semibold ${getTransactionColor(tx.tipo)}`}>
                    {getTransactionSign(tx.tipo)}{formatAmount(tx.valor)}
                  </p>
                  {tx.tipo === 'TOPUP' && Number(tx.fee_cve) > 0 && (
                    <p className="text-[10px] text-pagali-gray-400 mt-0.5">
                      taxa {formatAmount(Number(tx.fee_cve))}
                    </p>
                  )}
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      tx.status === 'COMPLETED'
                        ? 'bg-green-50 text-green-600'
                        : tx.status === 'PENDING'
                        ? 'bg-yellow-50 text-yellow-600'
                        : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {tx.status === 'COMPLETED' ? t('wallet.completedStatus') : tx.status === 'PENDING' ? t('wallet.pendingStatus') : t('wallet.failedStatus')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Top-up Modal */}
      <Modal
        isOpen={topupModalOpen}
        onClose={closeTopupModal}
        title={
          topupStep === 'success'
            ? 'Carregamento efectuado!'
            : topupStep === 'quote' || topupStep === 'executing'
              ? t('wallet.topupWallet') + ' — Taxa'
              : t('wallet.topupWallet')
        }
        footer={
          topupStep === 'success' ? (
            <Button onClick={closeTopupModal} className="w-full">Fechar</Button>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="ghost"
                disabled={topupStep === 'executing'}
                onClick={topupStep === 'quote' ? () => { setTopupStep('input'); setTopupError(null); } : closeTopupModal}
              >
                {topupStep === 'quote' || topupStep === 'executing' ? t('common.back') : t('common.cancel')}
              </Button>
              <Button onClick={handleTopup} disabled={saving || topupStep === 'executing'}>
                {saving || topupStep === 'executing'
                  ? t('common.processing')
                  : topupStep === 'quote'
                    ? t('wallet.topup') + ' — Confirmar'
                    : t('wallet.topup')}
              </Button>
            </div>
          )
        }
      >
        {topupStep === 'success' ? (
          <div className="text-center py-4 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-pagali-gray-800">
                +{topupSuccessAmount.toLocaleString()} CVE
              </p>
              <p className="text-pagali-gray-500 text-sm mt-1">adicionados à tua wallet Pagali</p>
            </div>
          </div>
        ) : topupStep === 'quote' && topupQuote ? (
          <div className="space-y-4">
            <div className="bg-pagali-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-pagali-gray-600">Valor creditado na carteira</span>
                <span className="font-medium">{topupQuote.netCreditedCVE.toLocaleString()} CVE</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-pagali-gray-600">Taxa de serviço</span>
                <span className="font-medium text-amber-600">+ {topupQuote.feeCVE.toLocaleString()} CVE</span>
              </div>
              <div className="border-t border-pagali-gray-200 pt-3 flex justify-between">
                <span className="font-semibold text-pagali-gray-800">Total debitado</span>
                <span className="font-bold text-pagali-primary text-lg">{topupQuote.grossAmountCVE.toLocaleString()} CVE</span>
              </div>
            </div>
            <p className="text-xs text-pagali-gray-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
              {topupQuote.disclosureText}
            </p>
            {topupError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                {topupError}
              </div>
            )}
          </div>
        ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
              {t('wallet.valueCVE')}
            </label>
            <input
              type="number"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
              className="w-full px-4 py-3 text-lg border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none"
              placeholder="0"
            />
          </div>
          <div className="flex gap-2">
            {[1000, 2500, 5000, 10000].map((val) => (
              <button
                key={val}
                onClick={() => setTopupAmount(String(val))}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                  topupAmount === String(val)
                    ? 'bg-pagali-primary text-white'
                    : 'bg-pagali-gray-50 text-pagali-gray-600 hover:bg-pagali-gray-100'
                }`}
              >
                {val.toLocaleString()}
              </button>
            ))}
          </div>

          {/* Payment Method - Saved Cards */}
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-2">
              {t('wallet.paymentMethodLabel')}
            </label>

            {/* SISP Gateway option */}
            <button
              onClick={() => setSelectedCardId(null)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors mb-2 ${
                !selectedCardId
                  ? 'border-pagali-primary bg-pagali-primary/5'
                  : 'border-pagali-gray-200 hover:border-pagali-gray-300'
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-xs font-bold">SISP</span>
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-medium text-pagali-gray-800">VINTI4 / SISP</p>
                <p className="text-xs text-pagali-gray-500">{t('wallet.redirectToGateway')}</p>
              </div>
              {!selectedCardId && (
                <div className="w-5 h-5 rounded-full bg-pagali-primary flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>

            {/* Saved Cards */}
            {savedCards.length > 0 && (
              <div className="space-y-2">
                {savedCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setSelectedCardId(card.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                      selectedCardId === card.id
                        ? 'border-pagali-primary bg-pagali-primary/5'
                        : 'border-pagali-gray-200 hover:border-pagali-gray-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      card.cardType === 'VISA' ? 'bg-blue-100' : card.cardType === 'MASTERCARD' ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      <span className={`text-xs font-bold ${
                        card.cardType === 'VISA' ? 'text-blue-600' : card.cardType === 'MASTERCARD' ? 'text-red-600' : 'text-green-700'
                      }`}>
                        {card.cardType === 'VISA' ? 'VISA' : card.cardType === 'MASTERCARD' ? 'MC' : 'V4'}
                      </span>
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-pagali-gray-800">
                        {card.alias || card.cardType} •••• {card.lastFourDigits}
                      </p>
                      <p className="text-xs text-pagali-gray-500">
                        {card.cardHolder}
                        {card.expireMonth && card.expireYear
                          ? ` · ${card.expireMonth}/${card.expireYear}`
                          : card.limitDate
                            ? ` · válido até ${fmtParede(card.limitDate, { month: '2-digit', year: 'numeric' }, 'pt-PT')}`
                            : ''}
                      </p>
                    </div>
                    {card.isDefault === 'S' && (
                      <span className="text-xs bg-pagali-primary/10 text-pagali-primary px-2 py-0.5 rounded-full font-medium">
                        {t('wallet.defaultCard')}
                      </span>
                    )}
                    {selectedCardId === card.id && (
                      <div className="w-5 h-5 rounded-full bg-pagali-primary flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-pagali-gray-400">
            {selectedCardId
              ? t('wallet.chargeFromSavedCard')
              : t('wallet.paymentProcessedViaSISP')
            }
          </p>
        </div>
        )}
      </Modal>

      {/* PagaMe Modal — Criar/Resultado */}
      <Modal
        isOpen={pagameModalOpen}
        onClose={() => { setPagameModalOpen(false); setCreatedLink(null); }}
        title={createdLink ? 'Link Criado!' : 'Criar Link PagaMe'}
        footer={
          createdLink ? (
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => { setPagameModalOpen(false); setCreatedLink(null); }}>
                Fechar
              </Button>
              <Button onClick={() => handleShareLink(createdLink.url, createdLink.descricao)}>
                <FiShare2 className="mr-1" /> Partilhar
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setPagameModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreatePagame} disabled={saving}>
                {saving ? 'A criar...' : 'Criar Link'}
              </Button>
            </div>
          )
        }
      >
        {createdLink ? (
          <div className="space-y-4">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <FiLink className="text-3xl text-green-500 mx-auto mb-2" />
              <p className="text-green-800 font-medium mb-1">O teu link PagaMe está pronto!</p>
              <p className="text-green-600 text-sm">Partilha-o com quem te deve pagar</p>
            </div>
            <div className="bg-pagali-gray-50 rounded-xl p-4">
              <p className="text-xs text-pagali-gray-500 mb-1">Link de pagamento:</p>
              <div className="flex items-center gap-2">
                <code className="text-sm text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg flex-1 truncate">
                  {createdLink.url}
                </code>
                <button onClick={() => handleCopyLink(createdLink.url)} className="p-2 hover:bg-pagali-gray-200 rounded-lg">
                  <FiCopy className="text-pagali-gray-600" />
                </button>
              </div>
            </div>
            <div className="text-sm text-pagali-gray-600">
              <p><strong>Descrição:</strong> {createdLink.descricao}</p>
              <p><strong>Valor:</strong> {createdLink.valor ? `${createdLink.valor.toLocaleString()}$00 ECV` : 'Valor aberto (o pagador escolhe)'}</p>
              <p><strong>Tipo:</strong> {createdLink.tipo === 'UNICO' ? 'Pagamento único' : 'Aceita múltiplos pagamentos'}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-pagali-gray-500">
              Cria um link de pagamento para receber dinheiro. Qualquer pessoa pode pagar via Vinti4, mesmo sem conta Pagali.
            </p>
            <div>
              <label className="block text-sm font-medium text-pagali-gray-700 mb-1">Descrição *</label>
              <input
                type="text"
                value={pagameForm.descricao}
                onChange={(e) => setPagameForm({ ...pagameForm, descricao: e.target.value })}
                className="w-full px-4 py-2 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none"
                placeholder="Ex: Almoço de equipa, Renda Janeiro..."
                maxLength={255}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-pagali-gray-700 mb-1">Valor (CVE)</label>
              <input
                type="number"
                value={pagameForm.valor}
                onChange={(e) => setPagameForm({ ...pagameForm, valor: e.target.value })}
                className="w-full px-4 py-2 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none"
                placeholder="Deixa vazio para valor aberto"
              />
              <p className="text-xs text-pagali-gray-400 mt-1">Deixa vazio se queres que o pagador escolha o valor</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pagali-gray-700 mb-1">Tipo</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setPagameForm({ ...pagameForm, tipo: 'MULTIPLO' })}
                  className={`flex-1 py-2 px-3 rounded-xl border text-sm ${
                    pagameForm.tipo === 'MULTIPLO'
                      ? 'border-pagali-primary bg-pagali-primary/10 text-pagali-primary font-medium'
                      : 'border-pagali-gray-300 text-pagali-gray-600'
                  }`}
                >
                  Múltiplos pagamentos
                </button>
                <button
                  onClick={() => setPagameForm({ ...pagameForm, tipo: 'UNICO' })}
                  className={`flex-1 py-2 px-3 rounded-xl border text-sm ${
                    pagameForm.tipo === 'UNICO'
                      ? 'border-pagali-primary bg-pagali-primary/10 text-pagali-primary font-medium'
                      : 'border-pagali-gray-300 text-pagali-gray-600'
                  }`}
                >
                  Pagamento único
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Card Info Modal */}
      <Modal
        isOpen={addCardInfoOpen}
        onClose={() => setAddCardInfoOpen(false)}
        title="Adicionar Cartão Vinti4"
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setAddCardInfoOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddCardConfirm} disabled={saving || !vinti4Confirmado}>
              Continuar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Só cartões Vinti4 podem ser tokenizados (SISP MOP023.01 §3.3). Cartões
              Visa/Mastercard chegam a ficar guardados mas os pagamentos falham
              depois — avisar antes evita o cartão inútil na carteira. */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
            <svg className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 10a2 2 0 012-2h14a2 2 0 012 2M3 10v8a2 2 0 002 2h14a2 2 0 002-2v-8M4 4l16 16" />
            </svg>
            <div>
              <h4 className="font-semibold text-sm text-red-900">Apenas cartões Vinti4</h4>
              <p className="text-xs text-red-800 mt-1 leading-relaxed">
                Só é possível guardar cartões da rede <strong>Vinti4</strong>. Cartões Visa ou Mastercard
                não são aceites para pagamentos guardados — para esses, usa a opção &ldquo;Pagar com Cartão&rdquo;.
              </p>
              <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={vinti4Confirmado}
                  onChange={(e) => setVinti4Confirmado(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-xs font-semibold text-red-900">
                  Confirmo que o meu cartão é da rede Vinti4
                </span>
              </label>
            </div>
          </div>

          <p className="text-sm text-pagali-gray-600">
            Vais ser redirecionado para a página segura do <strong>SISP Vinti4</strong> para guardar o teu cartão. Nenhum valor será cobrado agora.
          </p>

          <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <h4 className="font-semibold text-sm text-blue-900">Como funciona?</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <div className="flex gap-2">
                <span className="font-bold text-blue-600">1.</span>
                <span>Insere os dados do teu cartão na página segura do SISP</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold text-blue-600">2.</span>
                <span>Confirma com o código OTP enviado para o teu telemóvel</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold text-blue-600">3.</span>
                <span>O cartão fica guardado para pagamentos futuros sem precisares de inserir os dados novamente</span>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 rounded-xl p-4 space-y-2">
            <h4 className="font-semibold text-sm text-amber-900">Na página do SISP vais definir:</h4>
            <ul className="text-sm text-amber-800 space-y-1.5">
              <li className="flex gap-2">
                <FiCreditCard className="mt-0.5 flex-shrink-0" />
                <span><strong>N.º de pagamentos</strong> — quantas vezes o cartão pode ser usado (máximo da SISP: 99)</span>
              </li>
              <li className="flex gap-2">
                <FiTrendingUp className="mt-0.5 flex-shrink-0" />
                <span><strong>Valor máximo total</strong> — o limite acumulado de todos os pagamentos (ex: 50.000$00)</span>
              </li>
              <li className="flex gap-2">
                <FiShield className="mt-0.5 flex-shrink-0" />
                <span><strong>Data limite</strong> — até quando o cartão pode ser usado (ex: 31/12/2027)</span>
              </li>
            </ul>
            <p className="text-xs text-amber-700 mt-2">
              Podes alterar estes limites ou remover o cartão a qualquer momento.
            </p>
          </div>

          <div className="flex items-start gap-2 text-xs text-pagali-gray-500">
            <FiShield className="mt-0.5 flex-shrink-0 text-green-500" />
            <span>Os dados do cartão são processados diretamente pelo SISP. A Pagali nunca tem acesso ao número completo do cartão.</span>
          </div>
        </div>
      </Modal>

      {/* PIN Modal */}
      <Modal
        isOpen={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        title={t('wallet.configurePin')}
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setPinModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSetPin} disabled={saving}>
              {saving ? t('common.saving') : t('wallet.savePin')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
              {t('wallet.currentPin')}
            </label>
            <input
              type="password"
              maxLength={4}
              value={pinData.pin_atual}
              onChange={(e) => setPinData({ ...pinData, pin_atual: e.target.value })}
              className="w-full px-4 py-2 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none tracking-widest text-center text-xl"
              placeholder="••••"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
              {t('wallet.newPin')}
            </label>
            <input
              type="password"
              maxLength={4}
              value={pinData.novo_pin}
              onChange={(e) => setPinData({ ...pinData, novo_pin: e.target.value })}
              className="w-full px-4 py-2 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none tracking-widest text-center text-xl"
              placeholder="••••"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
              {t('wallet.confirmPin')}
            </label>
            <input
              type="password"
              maxLength={4}
              value={pinData.confirmar_pin}
              onChange={(e) => setPinData({ ...pinData, confirmar_pin: e.target.value })}
              className="w-full px-4 py-2 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none tracking-widest text-center text-xl"
              placeholder="••••"
            />
          </div>
          <p className="text-xs text-pagali-gray-400 flex items-center gap-1">
            <FiShield className="text-pagali-primary" />
            {t('wallet.pinProtectsTransfers')}
          </p>
        </div>
      </Modal>

      {/* BCN Top-up Modal — Recarga por Transferência Bancária */}
      <Modal
        isOpen={bcnModalOpen}
        onClose={closeBcnModal}
        title={
          bcnStep === 'confirmed'
            ? 'Recarga confirmada!'
            : 'Carregar por transferência BCN'
        }
        footer={
          bcnStep === 'input' ? (
            <div className="flex gap-3">
              <Button variant="ghost" onClick={closeBcnModal}>Cancelar</Button>
              <Button onClick={handleBcnTopupInitiate} disabled={bcnLoading || !bcnAmount}>
                {bcnLoading ? 'A gerar...' : 'Gerar referência'}
              </Button>
            </div>
          ) : bcnStep === 'confirmed' ? (
            <Button onClick={closeBcnModal} className="w-full">Fechar</Button>
          ) : null
        }
      >
        {bcnStep === 'input' && (
          <div className="space-y-4">
            <p className="text-sm text-pagali-gray-500">
              Faz uma transferência BCN para a conta Pagali com a referência gerada. O saldo é creditado automaticamente após confirmação.
            </p>
            <div>
              <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                Valor (CVE)
              </label>
              <input
                type="number"
                value={bcnAmount}
                onChange={(e) => setBcnAmount(e.target.value)}
                className="w-full px-4 py-3 text-lg border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none"
                placeholder="0"
                min={100}
              />
            </div>
            <div className="flex gap-2">
              {[1000, 2500, 5000, 10000].map((val) => (
                <button
                  key={val}
                  onClick={() => setBcnAmount(String(val))}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                    bcnAmount === String(val)
                      ? 'bg-pagali-primary text-white'
                      : 'bg-pagali-gray-50 text-pagali-gray-600 hover:bg-pagali-gray-100'
                  }`}
                >
                  {val.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
              <p className="font-semibold mb-1">Como funciona:</p>
              <ol className="space-y-1 list-decimal list-inside">
                <li>Gera a referência abaixo</li>
                <li>Faz a transferência BCN com a referência no campo "Descrição"</li>
                <li>O saldo é creditado automaticamente</li>
              </ol>
            </div>
          </div>
        )}

        {bcnStep === 'pending' && bcnData && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-amber-800 font-medium text-sm">A aguardar confirmação de pagamento</p>
              <p className="text-amber-600 text-xs mt-1">Verificação automática a cada 30 segundos</p>
            </div>

            <div className="space-y-3">
              <div className="bg-pagali-gray-50 rounded-xl p-4">
                <p className="text-xs text-pagali-gray-500 mb-1">IBAN Pagali (destino)</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono text-pagali-gray-800 flex-1">{bcnData.iban}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(bcnData.iban); showSuccess('IBAN copiado'); }}
                    className="p-1.5 hover:bg-pagali-gray-200 rounded-lg transition-colors"
                  >
                    <FiCopy className="text-pagali-gray-500 text-sm" />
                  </button>
                </div>
              </div>

              <div className="bg-pagali-gray-50 rounded-xl p-4">
                <p className="text-xs text-pagali-gray-500 mb-1">Referência (copiar para o campo "Descrição")</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono font-bold text-pagali-primary flex-1">{bcnData.ref}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(bcnData.ref); showSuccess('Referência copiada'); }}
                    className="p-1.5 hover:bg-pagali-gray-200 rounded-lg transition-colors"
                  >
                    <FiCopy className="text-pagali-gray-500 text-sm" />
                  </button>
                </div>
              </div>

              <div className="bg-pagali-gray-50 rounded-xl p-4">
                <p className="text-xs text-pagali-gray-500 mb-1">Valor a transferir</p>
                <p className="text-lg font-bold text-pagali-gray-800">{parseFloat(bcnData.amount_cve).toLocaleString('pt-PT')}$00 ECV</p>
              </div>
            </div>

            <p className="text-xs text-pagali-gray-400 text-center">
              A janela pode ser fechada. Receberás uma notificação quando o pagamento for confirmado.
            </p>
          </div>
        )}

        {bcnStep === 'confirmed' && bcnData && (
          <div className="space-y-4 text-center py-2">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-pagali-gray-800">
                +{parseFloat(bcnData.amount_cve).toLocaleString('pt-PT')}$00
              </p>
              <p className="text-pagali-gray-500 text-sm mt-1">adicionados à tua wallet Pagali</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-xs text-green-700">
              <p>Ref: <strong>{bcnData.ref}</strong></p>
            </div>
          </div>
        )}
      </Modal>

      {/* Rename Card Modal */}
      <Modal
        isOpen={renameModalOpen}
        onClose={() => { setRenameModalOpen(false); setRenameCardId(null); }}
        title={t('wallet.renameCard')}
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => { setRenameModalOpen(false); setRenameCardId(null); }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRenameCard} disabled={saving}>
              {saving ? t('common.processing') : t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="py-2">
          <p className="text-sm text-pagali-gray-600 mb-3">{t('wallet.renameCardDesc')}</p>
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            maxLength={50}
            placeholder={t('wallet.cardNamePlaceholder')}
            className="w-full px-4 py-2.5 border border-pagali-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pagali-primary/50"
            autoFocus
          />
          <p className="text-xs text-pagali-gray-400 mt-2">{t('wallet.renameCardHint')}</p>
        </div>
      </Modal>

      {/* Delete Card Confirmation Modal */}
      <Modal
        isOpen={deleteCardModalOpen}
        onClose={() => { setDeleteCardModalOpen(false); setDeleteCardId(null); }}
        title={t('wallet.removeCard')}
        footer={
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => { setDeleteCardModalOpen(false); setDeleteCardId(null); }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteCard}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? t('common.removing') : t('common.remove')}
            </Button>
          </div>
        }
      >
        <div className="text-center py-4">
          <FiTrash2 className="text-4xl text-red-500 mx-auto mb-3" />
          <p className="text-pagali-gray-700">
            {t('wallet.removeCardConfirm')}
          </p>
          <p className="text-sm text-pagali-gray-500 mt-2">
            {t('wallet.removeCardDesc')}
          </p>
        </div>
      </Modal>

      {/* Modal: Pagamento QR com token */}
      <Modal
        isOpen={qrPayOpen}
        onClose={closeQrPay}
        title="Pagar com Token Pagali"
        footer={
          qrPayResult?.success ? (
            <Button onClick={closeQrPay}>Fechar</Button>
          ) : (
            <div className="flex gap-3">
              <Button variant="ghost" onClick={closeQrPay}>Cancelar</Button>
              <Button onClick={handleQrTokenPay} disabled={!qrPayCard || qrPayLoading}>
                {qrPayLoading ? 'A processar...' : 'Confirmar pagamento'}
              </Button>
            </div>
          )
        }
      >
        {qrPayDetails ? (
          <div className="bg-purple-50 rounded-xl p-4 mb-4 text-center">
            <p className="text-2xl font-bold text-pagali-primary">
              {qrPayDetails.amount.toLocaleString('pt-PT')}$00
            </p>
            {(qrPayDetails.entityCode || qrPayDetails.referenceNumber) && (
              <p className="text-sm text-pagali-gray-500 mt-1">
                {qrPayDetails.entityCode ? `Entidade ${qrPayDetails.entityCode}` : ''}
                {qrPayDetails.entityCode && qrPayDetails.referenceNumber ? ' · ' : ''}
                {qrPayDetails.referenceNumber ? `Ref: ${qrPayDetails.referenceNumber}` : ''}
              </p>
            )}
          </div>
        ) : (
          <div className="flex justify-center py-4"><LoaderComponent size="sm" /></div>
        )}

        {qrPayResult?.success ? (
          <div className="text-center py-4">
            <div className="text-green-500 text-4xl mb-3">✓</div>
            <p className="font-semibold text-green-700">Pagamento efectuado com sucesso!</p>
          </div>
        ) : qrPayResult ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <p className="text-red-700 text-sm">{qrPayResult.message || 'Pagamento não autorizado'}</p>
          </div>
        ) : null}

        {!qrPayResult?.success && (
          <>
            <p className="text-sm text-pagali-gray-600 mb-3">Selecciona o cartão guardado com que queres pagar:</p>
            {savedCards.length === 0 ? (
              <div className="text-center py-6 text-pagali-gray-400">
                <FiCreditCard className="text-3xl mx-auto mb-2" />
                <p className="text-sm">Não tens cartões guardados na Wallet.</p>
                <button
                  onClick={() => { closeQrPay(); setAddCardInfoOpen(true); }}
                  className="mt-3 text-pagali-primary text-sm font-medium underline"
                >
                  Adicionar Pagali Card
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {savedCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setQrPayCard(card.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${qrPayCard === card.id ? 'border-pagali-primary bg-pagali-primary/5' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <FiCreditCard className={qrPayCard === card.id ? 'text-pagali-primary' : 'text-gray-400'} />
                    <span className="text-sm font-medium">{card.alias || card.cardType || 'Cartão'}</span>
                    {qrPayCard === card.id && (
                      <svg className="ml-auto w-4 h-4 text-pagali-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Wallet;
